import { html, render, ifDefined, unsafeHTML } from '@bolt/element';
import { iconMore, iconClose } from '@bolt/elements-icon';
import '@bolt/core/utils/optimized-resize';

// @todo this JS could be shared between Navbar and Tabs, nothing Navbar-specific here
export class BoltOverflowMenu {
  constructor(el, options = {}) {
    if (!el) return;
    this.el = el;
    this.options = {
      items: [],
      container: null,
      baseClass: 'c-bolt-overflow-menu',
      moreText: 'More',
      ...options,
    };

    this.init();
    this.destroy();
  }

  init() {
    this.handleMenuItemClick = this.handleMenuItemClick.bind(this);
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('throttledResize', this.handleResize);
    window.addEventListener('scroll', this.hideDropdownMenuOnScroll.bind(this));

    this.menuItems = this.getMenuItems();
    this.theme = this.getTheme();

    this.el.classList.add(this.options.baseClass);

    this.updateMenu();

    // Assign after updateMenu, which renders the popover element
    this.popover = this.el.querySelector('bolt-popover');

    this.handleResize();
  }

  destroy() {
    window.removeEventListener(
      'scroll',
      this.hideDropdownMenuOnScroll.bind(this),
    );
  }

  getMenuItems() {
    return this.options.items.map((item) => {
      const menuItem = item.cloneNode(true);

      const menuItemElement = menuItem.querySelector('a, button');

      if (menuItemElement) {
        // Replace class for dropdown menu item styling
        menuItemElement.className = 'c-bolt-navbar-menu__dropdown-item';

        // Attach event listener only if it's an <a> element
        if (menuItemElement.tagName.toLowerCase() === 'a') {
          menuItemElement.addEventListener('click', this.handleMenuItemClick);
        }
      }

      return menuItem;
    });
  }

  handleMenuItemClick(event) {
    event.preventDefault();
    const target = event.target.closest('a');
    const index = this.menuItems.findIndex((el) => el.contains(target));

    if (index !== -1) {
      this.options.items[index]?.querySelector('a')?.click();
    }
  }

  hideDropdownMenuOnScroll() {
    if (this.popover?.tippyInstance?.state.isVisible) {
      this.popover.tippyInstance.hide();
    }
  }

  handleResize() {
    const { items, container } = this.options;

    if (!container) return;

    container.classList.add('u-bolt-overflow-hidden');

    // reveal all items for the calculation and remove tabindex from inner anchor tags
    items.forEach((item) => {
      item.classList.remove('u-bolt-hidden');
      const tag = item.querySelector('a, button');
      if (tag) {
        tag.removeAttribute('tabindex');
      }
    });

    // hide all duplicate items in the menu and set tabindex to inner anchor tags
    this.menuItems.forEach((item) => {
      item.classList.add('u-bolt-hidden');
      const tag = item.querySelector('a, button');
      if (tag) {
        tag.setAttribute('tabindex', '-1');
      }
    });
    // Note: below we use `getBoundingClientRect()` because it returns a decimal.
    // Whereas `offsetWidth` returns an integer, leading to rounding errors in
    // Safari and older Edge < 80.
    const containerWidth = container.getBoundingClientRect().width;
    const hiddenItems = [];
    let menuWidth = this.el.getBoundingClientRect().width;
    let isOverflowing = false;

    items.forEach((item, i) => {
      // Subtract 1 from the width of each item to fix a bug in Edge version < 80 where
      // it miscalculates the size of the items and/or container and always adds at least
      // 1 item to the "More" dropdown menu.
      const itemWidth = item.getBoundingClientRect().width - 1;
      // make sure the items fit + we haven't already started to encounter items that don't
      if (containerWidth >= menuWidth + itemWidth && isOverflowing !== true) {
        menuWidth += itemWidth;
      } else {
        isOverflowing = true;
        item.classList.add('u-bolt-hidden');
        const tag = item.querySelector('a, button');
        if (tag) {
          tag.setAttribute('tabindex', '-1');
        }
        hiddenItems.push(i);
      }
    });

    if (!hiddenItems.length) {
      this.el.classList.add('u-bolt-hidden');
      this.popover?.tippyInstance?.hide();
    } else {
      this.menuItems.forEach((item, i) => {
        if (hiddenItems.includes(i)) {
          item.classList.remove('u-bolt-hidden');
          const tag = item.querySelector('a, button');
          if (tag) {
            tag.removeAttribute('tabindex');
          }
        }
      });
      this.el.classList.remove('u-bolt-hidden');
    }

    container.classList.remove('u-bolt-overflow-hidden');
  }

  updateActiveItem(item) {
    const index = this.options.items.indexOf(item);

    if (index === -1) return;

    this.menuItems.forEach((el, i) => {
      if (i === index) {
        el.setAttribute('data-bolt-current', '');
      } else {
        el.removeAttribute('data-bolt-current');
      }
    });
  }

  getTheme() {
    const navbar = this.el.closest('.c-bolt-navbar');
    const themeClass = Array.from(navbar.classList).find((el) =>
      el.startsWith('t-bolt-'),
    );

    if (themeClass) {
      return themeClass.split('t-bolt-')[1];
    }
  }

  updateMenu() {
    if (!this.menuItems) return;

    const { baseClass, moreText } = this.options;

    render(
      html`
        <bolt-popover
          class="c-bolt-navbar-menu__popover"
          spacing="none"
          theme="${ifDefined(this.theme)}">
          <button type="button" class="${baseClass}__button">
            <span class="${baseClass}__button-text">${moreText}</span>
            <span class="${baseClass}__button-icon">
              ${unsafeHTML(iconMore())}
            </span>
            <span
              class="${baseClass}__button-icon ${baseClass}__button-icon--close">
              ${unsafeHTML(iconClose())}
            </span>
          </button>
          <div class="c-bolt-navbar-menu__content" slot="content">
            <ul class="c-bolt-navbar-menu__dropdown">
              ${this.menuItems}
            </ul>
          </div>
        </bolt-popover>
      `,
      this.el,
    );
  }
}
